// Store credit?
import React, { useContext, useState } from 'react';

// Components
import CreditCardForm from './CreditCardForm';
import SectionHeader from '../../../_molecules/SectionHeader';
import LoadingArea from '@molecules/LoadingArea';
import {
    BodyLarge,
    BodyMedium,
    BodySmall,
    DividerLightGreyStyled,
    ErrorText,
    H3,
    H4,
    LabelLarge,
    LinkSmall
} from '@styled';

// Functionality
import { BillingContext } from './BillingProvider';
import { capitalize } from '@general/utility';
import useViewer from '@hooks/useViewer';
import useDocumentTitle from '@hooks/useDocumentTitle';
import useStripeInstances from '@hooks/useStripeInstances';
import SectionBreadcrumbs from "../../../_molecules/SectionBreadcrumbs";
import SectionBreadcrumb from "../../../_molecules/SectionBreadcrumb";
import AccountMobileSubnav from "../Account/AccountMobileSubnav";

const BillingPaymentPreferencesLayout = () => {
    const {
        // Data
        cardTableErrorMessage,
        cardTableInfoMessage,
        generalErrorMessage,
        generalInfoMessage,
        isSubmitting,
        member,
        memberId,
        savedCards,
        setupIntentSecret,

        // Methods
        createStripeIntent,
        deletePaymentMethod,
        fetchStripeCustomer,
        setCardTableErrorMessage,
        setCardTableInfoMessage,
        setGeneralErrorMessage,
        setGeneralInfoMessage,
        setMember,
        setPaymentMethodAsDefault,
        updateMember,
    } = useContext(BillingContext);

    const [cardToEdit, setCardToEdit] = useState(null);
    const { viewer } = useViewer();
    const { documentTitle } = useDocumentTitle('Billing & Payments');
    const { stripeInstanceUS } = useStripeInstances();

    const createCard = async ({ cardNumberElement, cardValues }) => {
        if (!setupIntentSecret) {
            return;
        }

        $('#loading-overlay').css('display', 'flex');

        const cardPaymentData = {
            payment_method: {
                card: cardNumberElement,
                billing_details: {
                    name: cardValues.name,
                    address: {
                        postal_code: cardValues.address_zip,
                    },
                },
            },
        };

        const { error } = await stripeInstanceUS.confirmCardSetup(setupIntentSecret, cardPaymentData);

        if (error) {
            setGeneralErrorMessage(error.message);
        } else {
            await fetchStripeCustomer(memberId, true);
            setCardToEdit(null);
            setGeneralInfoMessage('Card saved successfully.');

            // Get new setup intent in case user is setting up multiple cards
            await createStripeIntent(memberId);
        }

        $('#loading-overlay').css('display', 'none');
    };

    const updateCard = () => {
        clearMessages();
        alert('Update');
    };

    const setCardAsDefault = async cardToSetAsDefault => {
        clearMessages();

        const confirmMsg = `Are you sure you want to set your ${capitalize(cardToSetAsDefault.type)} ending in ${
            cardToSetAsDefault.last4
        } as your default payment method?`;

        if (!confirm(confirmMsg)) { /* eslint-disable-line */
            return;
        }

        setCardTableInfoMessage('Setting card as default...');

        try {
            await setPaymentMethodAsDefault(memberId, cardToSetAsDefault.id);
            setCardTableInfoMessage('Card successfully set as default.');
        } catch (error) {
            //
        }
    };

    const removeCard = async cardToRemove => {
        clearMessages();

        const confirmMsg = `Are you sure you want to remove your ${capitalize(cardToRemove.type)} ending in ${
            cardToRemove.last4
        }?`;

        if (!confirm(confirmMsg)) { /* eslint-disable-line */
            return;
        }

        setCardTableInfoMessage('Removing saved card...');

        // If this is the only saved card, set member auto renew to false
        if (savedCards.length === 1) {
            await updateMember(memberId, { auto_renew: false });
            setMember({ ...member, auto_renew: false });
        }

        await deletePaymentMethod(cardToRemove.id);
        setCardTableInfoMessage('Card successfully removed.');
        setCardToEdit(null);
    };

    const clearMessages = () => {
        setCardTableInfoMessage(null);
        setCardTableErrorMessage(null);
        setGeneralInfoMessage(null);
        setGeneralErrorMessage(null);
    };

    if (!viewer) {
        return <LoadingArea />;
    }

    return (
        <div id="my-icsc-billing">
            <SectionHeader title="My Account" />

            <div className="flex justify-content-between mt-2">
                <SectionBreadcrumbs>
                    <SectionBreadcrumb route="/my-icsc/account">My Account</SectionBreadcrumb>
                    <SectionBreadcrumb route="/my-icsc/account/edit-billing">Billing & Payments</SectionBreadcrumb>
                    <SectionBreadcrumb isActive>Payment Preferences</SectionBreadcrumb>
                </SectionBreadcrumbs>
            </div>

            <H3 className="mt-3 mb-4">Payment Preferences</H3>

            <H4 className="mt-2rem mb-1rem">Adjust Payment Preferences</H4>

            <div className="saved-credit-card-section">
                {/* If user has a saved card, show this */}
                {savedCards.length > 0 && (
                    <div
                        id="saved-credit-card-form"
                        className="border-1 border-light-gray border-solid mb-1rem px-1.25rem py-1rem"
                    >
                        <div>
                            <div className="d-inline-block saved-card-column-1">
                                <LabelLarge>Your saved card</LabelLarge>
                            </div>

                            <div className="d-inline-block saved-card-column-3">
                                <LabelLarge>Expires on</LabelLarge>
                            </div>

                            <div className="d-inline-block saved-card-column-6">
                                <LabelLarge>Default?</LabelLarge>
                            </div>

                            <div className="d-inline-block saved-card-column-7" />
                            <div className="d-inline-block saved-card-column-8" />
                        </div>

                        <DividerLightGreyStyled className="mt-0.25rem mb-0.5rem" />

                        {savedCards.map(savedCard => (
                            <div key={savedCard.id}>
                                <div className="d-inline-block saved-card-column-1">
                                    <BodySmall>
                                        <span className="font-weight-bold">{savedCard.type}</span>
                                        <span className="font-weight-normal"> ending in {savedCard.last4}</span>
                                    </BodySmall>
                                </div>

                                <div className="d-inline-block saved-card-column-3">
                                    <BodySmall>
                                        {savedCard.exp_month}/{savedCard.exp_year}
                                    </BodySmall>
                                </div>

                                <div className="d-inline-block saved-card-column-6">
                                    {savedCard.default && <BodySmall>Y</BodySmall>}
                                </div>

                                <div className="d-inline-block saved-card-column-7">
                                    {!savedCard.default && (
                                        <LinkSmall
                                            href="#"
                                            onClick={e => {
                                                e.preventDefault();
                                                setCardAsDefault(savedCard);
                                            }}
                                        >
                                            Set as default
                                        </LinkSmall>
                                    )}
                                </div>

                                <div className="d-inline-block saved-card-column-8">
                                    <LinkSmall
                                        href="#"
                                        onClick={e => {
                                            e.preventDefault();
                                            removeCard(savedCard);
                                        }}
                                    >
                                        Remove
                                    </LinkSmall>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {cardTableInfoMessage && <BodyMedium>{cardTableInfoMessage}</BodyMedium>}
                {cardTableErrorMessage && (
                    <BodyMedium>
                        <ErrorText>{cardTableErrorMessage}</ErrorText>
                    </BodyMedium>
                )}

                <button
                    type="button"
                    className="createsavedcard btn btn-primary"
                    onClick={() => {
                        setGeneralInfoMessage(null);
                        setGeneralErrorMessage(null);
                        setCardToEdit({ name: '', address_zip: '' });
                    }}
                >
                    Add a New Card
                </button>

                {!!cardToEdit && (
                    <div className="mt-2rem">
                        <CreditCardForm
                            card={cardToEdit}
                            isSubmitting={isSubmitting}
                            onCardChange={setCardToEdit}
                            onSave={cardData => {
                                if (!cardData.id) {
                                    createCard(cardData);
                                } else {
                                    updateCard(cardData);
                                }
                            }}
                            onCancel={() => {
                                setCardToEdit(null);
                            }}
                        />

                        {generalInfoMessage && <BodyMedium>{generalInfoMessage}</BodyMedium>}
                        {generalErrorMessage && (
                            <BodyMedium>
                                <ErrorText>{generalErrorMessage}</ErrorText>
                            </BodyMedium>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BillingPaymentPreferencesLayout;
